import { CheckIcon, MinusIcon } from "@phosphor-icons/react";
import type { ComponentProps, ReactNode } from "react";
import {
  Checkbox as C,
  Focusable,
  Switch as S,
  Tabs as T,
  Tab,
  TabList,
  TooltipTrigger,
  Tooltip as TT,
} from "react-aria-components";
import { cn, initials } from "./utils";

export function Checkbox({
  checked,
  onCheckedChange,
  label,
  className,
}: {
  checked: boolean | "indeterminate";
  onCheckedChange: (checked: boolean) => void;
  label?: string;
  className?: string;
}) {
  return (
    // Keeps a click from also reaching a clickable row or card around the checkbox.
    // biome-ignore lint/a11y/noStaticElementInteractions: only stops propagation, the checkbox handles input
    // biome-ignore lint/a11y/useKeyWithClickEvents: keyboard input goes to the checkbox itself
    <span className="inline-flex" onClick={(e) => e.stopPropagation()}>
      <C
        isSelected={checked === true}
        isIndeterminate={checked === "indeterminate"}
        onChange={onCheckedChange}
        aria-label={label}
        className="group flex"
      >
        <span
          className={cn(
            "flex size-4 shrink-0 items-center justify-center rounded-[4px] border border-border-strong bg-bg text-white shadow-sm group-data-[focus-visible]:outline-2 group-data-[focus-visible]:outline-offset-2 group-data-[focus-visible]:outline-accent group-data-[indeterminate]:border-accent group-data-[indeterminate]:bg-accent group-data-[selected]:border-accent group-data-[selected]:bg-accent",
            className,
          )}
        >
          {checked === "indeterminate" ? (
            <MinusIcon className="size-3" weight="bold" />
          ) : (
            checked && <CheckIcon className="size-3" weight="bold" />
          )}
        </span>
      </C>
    </span>
  );
}

export function Switch({
  checked,
  onCheckedChange,
  id,
  disabled,
  children,
  className,
  "aria-label": ariaLabel,
}: {
  checked: boolean;
  onCheckedChange: (v: boolean) => void;
  id?: string;
  disabled?: boolean;
  /** Visible label, rendered after the switch and clickable. */
  children?: ReactNode;
  className?: string;
  "aria-label"?: string;
}) {
  return (
    <S
      id={id}
      isSelected={checked}
      isDisabled={disabled}
      aria-label={ariaLabel}
      onChange={onCheckedChange}
      className={cn("group flex items-center gap-3 data-[disabled]:opacity-60", className)}
    >
      <span className="relative h-5 w-9 shrink-0 rounded-full bg-border-strong transition-colors group-data-[focus-visible]:outline-2 group-data-[focus-visible]:outline-offset-2 group-data-[focus-visible]:outline-accent group-data-[selected]:bg-accent">
        <span className="absolute top-0.5 block size-4 translate-x-0.5 rounded-full bg-white shadow-sm transition-transform group-data-[selected]:translate-x-[18px]" />
      </span>
      {children}
    </S>
  );
}

export function Tabs({
  value,
  onValueChange,
  tabs,
  className,
}: {
  value: string;
  onValueChange: (v: string) => void;
  tabs: { value: string; label: ReactNode; count?: number }[];
  className?: string;
}) {
  return (
    <T selectedKey={value} onSelectionChange={(key) => onValueChange(String(key))}>
      <TabList className={cn("flex gap-4 border-b border-border", className)}>
        {tabs.map((tab) => (
          <Tab
            key={tab.value}
            id={tab.value}
            className="-mb-px flex h-9 cursor-default items-center gap-1.5 border-b-2 border-transparent text-[13px] font-medium text-fg-muted outline-none transition-colors data-[focus-visible]:text-fg data-[hovered]:text-fg data-[selected]:border-fg data-[selected]:text-fg"
          >
            {tab.label}
            {tab.count !== undefined && (
              <span className="rounded bg-muted px-1.5 text-[11px] tabular-nums text-fg-muted">
                {tab.count}
              </span>
            )}
          </Tab>
        ))}
      </TabList>
    </T>
  );
}

export function Tooltip({ content, children }: { content: ReactNode; children: ReactNode }) {
  return (
    <TooltipTrigger delay={250} closeDelay={0}>
      <Focusable>{children as ComponentProps<typeof Focusable>["children"]}</Focusable>
      <TT
        offset={6}
        className="animate-fade-in z-50 max-w-64 rounded-md bg-fg px-2 py-1 text-[12px] text-bg shadow-md"
      >
        {content}
      </TT>
    </TooltipTrigger>
  );
}

export function Avatar({ name, src, size = 28 }: { name: string; src?: string | null; size?: number }) {
  return src ? (
    <img
      src={src}
      alt=""
      className="shrink-0 rounded-full object-cover"
      style={{ width: size, height: size }}
    />
  ) : (
    <span
      className="flex shrink-0 items-center justify-center rounded-full bg-muted font-medium text-fg-muted"
      style={{ width: size, height: size, fontSize: Math.max(10, size * 0.38) }}
    >
      {initials(name)}
    </span>
  );
}

export function Skeleton({ className }: { className?: string }) {
  return <div className={cn("animate-soft-pulse rounded-md bg-muted", className)} />;
}

export function EmptyState({
  icon,
  title,
  description,
  action,
}: {
  icon: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center justify-center px-6 py-14 text-center">
      <div className="mb-3 flex size-10 items-center justify-center rounded-lg border border-border bg-subtle text-fg-muted [&_svg]:size-5">
        {icon}
      </div>
      <h3 className="text-[14px] font-semibold">{title}</h3>
      {description && <p className="mt-1 max-w-sm text-[13px] text-fg-muted">{description}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export function Kbd({ children }: { children: ReactNode }) {
  return (
    <kbd className="rounded border border-border bg-subtle px-1 font-mono text-[11px] text-fg-muted">
      {children}
    </kbd>
  );
}
